/**
 * Types for Rover status notifications.
 * These mirror the API contract in backend/openapi.yaml.
 * If one changes, change the other.
 */

export type Channel = 'email' | 'sms' | 'onscreen';
export const CHANNELS: Channel[] = ['email', 'sms', 'onscreen'];
export const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  onscreen: 'On-screen',
};

/** Where a status's message comes from. Computed by the server. */
export type MessageSource = 'default' | 'custom' | 'options';

export type SelectionMode = 'single' | 'multiple';
export type OptionsFormat = 'bullets' | 'paragraph';

/** Message content per channel. `null` means "not customised — use the default". */
export interface ChannelContent {
  email: { subject: string | null; body: string | null };
  sms: { body: string | null };
  onscreen: { title: string | null; body: string | null };
}

export type ChannelEnabled = Record<Channel, boolean>;

/** One row in the Notifications tab status list. */
export interface StatusNotificationSummary {
  status: string; // stable key, e.g. "deny"
  statusLabel: string; // display name, e.g. "Deny"
  source: MessageSource;
  enabled: ChannelEnabled;
  activeOptionCount: number;
}

/** Full settings for one status on one form. */
export interface StatusNotificationConfig extends StatusNotificationSummary {
  content: ChannelContent;
  /** System default message for this status. Read-only. Used for fallback and "Reset to default". */
  defaults: ChannelContent;
  optionsSelectionMode: SelectionMode;
  optionsFormat: OptionsFormat;
  updatedAt: string | null;
  updatedBy: { id: number; name: string } | null;
}

export interface UpdateStatusNotificationRequest {
  enabled?: Partial<ChannelEnabled>;
  content?: ChannelContent;
  optionsSelectionMode?: SelectionMode;
  optionsFormat?: OptionsFormat;
}

export interface MessageOption {
  id: string;
  label: string; // shown to staff; used in SMS
  messageText: string; // inserted into email and on-screen
  isActive: boolean;
  sortOrder: number;
}

export interface MessageOptionInput {
  label: string;
  messageText: string;
  isActive?: boolean;
}

export type MergeValues = Partial<Record<MergeField, string>>;

export const MERGE_FIELDS = ['applicant_name', 'program_name', 'status', 'application_id', 'selected_options'] as const;
export type MergeField = (typeof MERGE_FIELDS)[number];

/** A message rendered for one channel. Plain text; the server turns email into HTML. */
export interface RenderedMessage {
  channel: Channel;
  source: MessageSource;
  subject?: string; // email
  title?: string; // on-screen
  body: string;
  unknownTokens: string[];
  sms?: { characters: number; segments: number; encoding: 'GSM-7' | 'UCS-2' };
}

export interface TestSendRequest {
  channel: Exclude<Channel, 'onscreen'>;
  /** Unsaved draft content, so staff can test before saving. */
  content?: ChannelContent;
}

export interface TestSendResponse {
  channel: Exclude<Channel, 'onscreen'>;
  sentTo: string;
}

/** Everything the status-change modal needs for one application and target status. */
export interface StatusChangeContext {
  applicationId: string;
  status: string;
  statusLabel: string;
  currentStatusLabel: string;
  requiresOptions: boolean;
  options: MessageOption[]; // active only, sorted
  optionsSelectionMode: SelectionMode;
  optionsFormat: OptionsFormat;
  enabled: ChannelEnabled;
  content: ChannelContent;
  defaults: ChannelContent;
  recipient: { name: string; email: string | null; phone: string | null };
  mergeValues: MergeValues;
}

export interface ChangeStatusRequest {
  status: string;
  selectedOptionIds?: string[];
}

export type ChannelResultState = 'queued' | 'skipped';
export interface ChannelResult {
  channel: Channel;
  state: ChannelResultState;
  reason?: 'channel_disabled' | 'no_phone' | 'no_email';
}

export interface ChangeStatusResponse {
  applicationId: string;
  status: string;
  notifications: ChannelResult[];
}

/** Standard API error shape (see frontend README). */
export interface ApiErrorBody {
  error: {
    code: 'VALIDATION_ERROR' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'INTERNAL_ERROR' | string;
    message: string;
    fields?: Record<string, string>;
  };
}
